import "@via/components/select/select.css";
import {
  hydratedOutput,
  OutputParameterOptions,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { cleanProcessName } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { displayNameOptions } from "@via/utils/form-validators.tsx";
import {
  OutputParameterPayload,
  SelectOption,
} from "@via/utils/interfaces.tsx";
import { type JSX, useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import Select from "react-select";
import { AppConfigurationForm } from "@via/pages/projects/pipelines/workflow/components/app-configuration-form";
import { MetadataTrackerConfiguration } from "@via/pages/projects/pipelines/workflow/components/metadata-tracker-configuration";

export default function EditOutputModal({
  data,
  onSubmit,
  onCancel,
  isEditingAllowed,
}): JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
  const [pubWebApp, setPubWebApp] = useState<
    OutputParameterPayload["pubWebApp"]
  >({});
  const {
    handleSubmit,
    register,
    control,
    setFocus,
    setValue,
    formState: { errors },
  } = useForm<OutputParameterPayload>({
    mode: "onChange",
  });

  const handleLabelOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const cleanedValue = cleanProcessName(e.target.value) ?? "";
    setValue("label", cleanedValue);
  };

  const pubWebWatch = useWatch({
    control,
    name: "pubWeb",
  });

  const [isPublishMetadataTrackerChecked, setIsPublishMetadaTrackerChecked] =
    useState(false);

  const isAppSelected = useMemo(() => {
    if (pubWebWatch) {
      return (pubWebWatch as [{ label: string; value: string }])?.some(
        item => item.value === "apps"
      );
    } else {
      return (selectedOptions as [{ label: string; value: string }])?.some(
        item => item.value === "apps"
      );
    }
  }, [pubWebWatch, selectedOptions]);

  useEffect(() => {
    if (data) {
      const merged = {
        ...hydratedOutput,
        label: data.label,
        ...data?.settings,
      };
      const options = merged?.pubWeb?.split(",") || []; // Split the string by commas to get an array of values for strict comparison
      const selected = options
        .map(value => OutputParameterOptions.find(obj => obj.value === value))
        .filter(Boolean);
      setFocus("label");
      setSelectedOptions(selected);
      setInitialized(true);
      setPubWebApp(merged?.pubWebApp);
      if (options.includes("apps")) {
        if (merged?.pubWebApp) {
          if (merged.pubWebApp.app && Array.isArray(merged.pubWebApp.app)) {
            setValue(
              "pubWebApp.app",
              merged.pubWebApp.app.map(item => +item)
            );
          }
          setValue("pubWebApp.extension", merged.pubWebApp.extension);
          setValue("pubWebApp.mountAppFile", merged.pubWebApp.mountAppFile);
        }
      }
      if (merged?.pubDmeta) {
        setValue("pubDmeta", {
          filename: merged.pubDmeta.filename,
          feature: merged.pubDmeta.feature,
        });
        setIsPublishMetadaTrackerChecked(true);
      }
    }
  }, [data]);

  const handleSubmitOutputChanges = form => {
    if (Array.isArray(form.pubWeb)) {
      form.pubWeb = form.pubWeb.map(item => item.value).join(",");
    }
    if (isAppSelected) {
      form.pubWebApp = {
        ...pubWebApp,
        app: form.pubWebApp.app.map(item => item.toString()),
        extension: form.pubWebApp.extension || "",
        mountAppFile: form.pubWebApp.mountAppFile
          ? form.pubWebApp.mountAppFile.toString()
          : "false",
      };
    } else {
      // remove pubWebApp if it's not selected
      delete form.pubWebApp;
    }
    // remove pubDmeta if it's not selected
    if (!isPublishMetadataTrackerChecked) {
      delete form.pubDmeta;
    } else if (form.pubDmeta) {
      // Target Collection is intentionally not configurable anymore
      delete form.pubDmeta.target;
    }
    onSubmit(form);
  };

  const handlePublishMetadataTrackerChange = e => {
    setIsPublishMetadaTrackerChecked(e.target.checked);
    if (e.target.checked) {
      setValue("pubDmeta", {
        filename: "",
        feature: "",
      });
    }
  };

  return (
    <form
      className="flex h-full w-[600px] flex-col divide-gray-200 bg-white"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleSubmitOutputChanges)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="modal-header">
          <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
            Edit Output
          </h3>
        </div>
      </div>
      <div className="modal-body pb-4">
        {initialized && (
          <div className="flex flex-1 flex-col justify-between">
            <div className="my-6 flex flex-row items-center">
              <label className="flex w-64 flex-row items-center text-sm font-medium leading-6 text-gray-900">
                Display Name
                <span className="pl-1 text-red-400">*</span>
              </label>
              <div className="mt-2 w-full">
                <input
                  {...register("label", displayNameOptions.label)}
                  type="text"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  defaultValue={data?.label}
                  onBlur={handleLabelOnBlur}
                  disabled={!isEditingAllowed}
                />
                {errors?.label && (
                  <span className="text-xs text-rose-500">
                    {errors.label.message}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row items-center">
              <label className="flex w-64 flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900">
                Publish to Web Directory
              </label>
              <div className="mt-2 w-full">
                <Controller
                  control={control}
                  name="pubWeb"
                  defaultValue={selectedOptions}
                  render={({ field: { onChange, ref, ...rest } }) => (
                    <Select
                      {...rest}
                      ref={ref}
                      options={OutputParameterOptions}
                      closeMenuOnSelect={false}
                      onChange={onChange}
                      isMulti={true}
                      classNamePrefix={"select"}
                      isDisabled={!isEditingAllowed}
                    />
                  )}
                />
              </div>
            </div>
            <AppConfigurationForm
              isAppSelected={isAppSelected}
              pubWebApp={pubWebApp}
              register={register}
              control={control}
              errors={errors}
              isDisabled={!isEditingAllowed}
            />
            <div className="flex flex-row items-center my-6">
              <label className="flex items-center gap-1 text-sm font-medium leading-6 text-gray-900">
                <input
                  type="checkbox"
                  className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  checked={isPublishMetadataTrackerChecked}
                  onChange={handlePublishMetadataTrackerChange}
                  disabled={!isEditingAllowed}
                />
                Publish to Sample Tracker
              </label>
            </div>
            {isPublishMetadataTrackerChecked && (
              <MetadataTrackerConfiguration
                control={control}
                errors={errors}
                isDisabled={!isEditingAllowed}
              />
            )}
          </div>
        )}
      </div>
      <div className="modal-footer flex justify-end space-x-2">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => onCancel()}>
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          disabled={!isEditingAllowed}>
          Save
        </button>
      </div>
    </form>
  );
}
